import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { CertificateStatusEnum, ItemTypeEnum } from '@share/common-types';

import {
  CertificateDetailHeader,
  DetailBackLink,
  DetailHeader,
  DetailTagsRow,
  DetailHero,
  DetailDescriptionSection,
  DetailEligibleCruiseLines,
  DetailBusinessRules,
  DetailPriceCard,
} from './components';
import { WalletItem } from '../wallet-drawer/components/wallet-item';
import type { CertificateTemplateDetailViewProps } from './types';

import styles from './styles.module.scss';
import { BlueButton } from '@share/components';

export const CertificateTemplateDetailView: React.FC<CertificateTemplateDetailViewProps> = ({
  name,
  validityMonths,
  totalPrice,
  savings,
  itemTemplates,
  eligibleCruiseLines,
  imageUrl,
  url,
  descriptionContent,
  averageRetailPrice,
  onBackClick,
  onBuyClick,
  backLabel,
  buyLabel,
  currency = '', // will be $ with empty string
  isCertificate = false,
  certificateCode,
  certificateStatus,
  certificatePurchasedAt,
  certificateExpiresAt,
  certificateItems = [],
  accountUsername = '',
  certificateUpdatedAt,
}) => {
  const intl = useIntl();
  const isCruiseCertificate = itemTemplates.some((t) => t.type === ItemTypeEnum.Cruise);
  const showCruiseLines =
    isCruiseCertificate && Array.isArray(eligibleCruiseLines) && eligibleCruiseLines.length > 0;

  const savingsPercent = useMemo(() => {
    const total = totalPrice ?? 0;
    const savingsVal = savings ?? 0;
    return total > 0 ? Math.round((savingsVal / total) * 100) : 0;
  }, [totalPrice, savings]);

  const expiryText = intl.formatMessage(
    { id: 'certificates.detail.expiryMonths' },
    { months: validityMonths },
  );

  const resolvedBackLabel =
    backLabel ?? intl.formatMessage({ id: 'certificates.detail.backToList' });
  const resolvedBuyLabel = buyLabel ?? intl.formatMessage({ id: 'certificates.detail.buy' });

  return (
    <div className={styles.wrapper}>
      <div className={styles.contentContainer}>
        {!isCertificate && <DetailBackLink label={resolvedBackLabel} onClick={onBackClick} />}

        <div className={styles.content}>
          <div className={styles.contentInnerContainer}>
            {isCertificate ? (
              <CertificateDetailHeader
                name={name}
                certificateCode={certificateCode ?? ''}
                status={certificateStatus ?? CertificateStatusEnum.Pending}
                purchasedAt={certificatePurchasedAt ?? ''}
                expiresAt={certificateExpiresAt ?? ''}
              />
            ) : (
              <DetailHeader
                name={name}
                expiryText={expiryText}
                totalPrice={totalPrice}
                currency={currency}
              />
            )}

            {!isCertificate && (
              <div className={styles.tagsRow}>
                <DetailTagsRow items={itemTemplates} />
                {onBuyClick && (
                  <BlueButton className={styles.buyButtonWrap} onClick={onBuyClick}>
                    {resolvedBuyLabel}
                  </BlueButton>
                )}
              </div>
            )}
          </div>

          <DetailHero
            imageUrl={imageUrl}
            fallbackUrl={url}
            savingsPercent={isCertificate ? 0 : savingsPercent}
          />

          <div className={styles.contentInnerContainer}>
            <div className={styles.mainGrid}>
              <div className={styles.leftColumn}>
                <DetailDescriptionSection descriptionContent={descriptionContent} />

                {showCruiseLines && eligibleCruiseLines && (
                  <DetailEligibleCruiseLines cruiseLines={eligibleCruiseLines} />
                )}

                <DetailBusinessRules />
              </div>

              {!isCertificate ? (
                <DetailPriceCard
                  itemTemplates={itemTemplates ?? []}
                  totalPrice={totalPrice}
                  currency={currency}
                  averageRetailPrice={averageRetailPrice}
                  savings={savings}
                />
              ) : (
                <aside
                  className={styles.productsAside}
                  aria-labelledby="certificate-products-heading"
                >
                  <div className={styles.productsCard}>
                    <h2 id="certificate-products-heading" className={styles.productsTitle}>
                      {intl.formatMessage({ id: 'certificates.detail.products' })}
                    </h2>
                    <ul className={styles.productsList}>
                      {certificateItems.map((item) => (
                        <li key={item.id} className={styles.productsListItem}>
                          <WalletItem
                            item={item}
                            accountUsername={accountUsername}
                            certificateCode={certificateCode ?? ''}
                            certificateTotalPrice={totalPrice}
                            certificateStatus={certificateStatus ?? CertificateStatusEnum.Pending}
                            certificateExpiresAt={certificateExpiresAt ?? ''}
                            certificateUpdatedAt={certificateUpdatedAt}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                </aside>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
